import React, { useRef } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, ShieldCheck, Truck, BadgeDollarSign, Sun,
  Sparkles, Eye, Clock, CheckCircle2, Camera, Wrench, Star, ArrowRight,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "@/components/ui/carousel";
import { Header } from "@/components/Header";
import { LogoLockup } from "@/components/LogoLockup";
import { SectionHeader } from "@/components/SectionHeader";
import { BeforeAfterShowcase } from "@/components/BeforeAfterShowcase";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { QuoteForm } from "@/components/QuoteForm";
import { LANDING } from "@/constants/testIds";

const PHONE = "7852881434";
const PHONE_DISPLAY = "785-288-1434";
const EMAIL = "alexkoehn49@gmail.com";

// Cross-platform "Click to Text" SMS link (works on both iOS and Android).
const SMS_BODY = "Hi Alex! I'd like a headlight restoration quote. Here is my car's year, make, and model:";
const SMS_HREF = `sms:+17852881434?&body=${encodeURIComponent(SMS_BODY)}`;

// Real before/after gallery (mobile-optimized, self-hosted images)
const GALLERY = [
  {
    src: "/gallery/tractor-ba.jpg",
    type: "ba",
    alt: "Before and after headlight restoration on a red lawn tractor",
    caption: "Lawn tractor — cloudy lens restored to crystal-clear.",
  },
  {
    src: "/gallery/truck-green.jpg",
    type: "after",
    alt: "Restored headlights on a green Chevy pickup truck",
    caption: "Chevy pickup — headlights brought back to like-new clarity.",
  },
  {
    src: "/gallery/van-progress.jpg",
    type: "progress",
    alt: "Headlight restoration in progress on a Chevy van",
    caption: "Chevy van — a restoration in progress on the driveway.",
  },
];

const TOWNS = [
  "Hiawatha", "St. Joseph (MO)", "Horton", "Sabetha", "Holton", "Seneca",
  "Falls City (NE)", "Troy", "Highland", "Robinson", "Fairview", "Morrill",
  "Powhattan", "White Cloud", "Everest", "Atchison", "Wathena",
  "Savannah (MO)", "Maryville (MO)",
];

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.9, ease: "easeOut" },
};

const Container = ({ children, className = "" }) => (
  <div className={`max-w-6xl mx-auto px-4 sm:px-6 ${className}`}>{children}</div>
);

function App() {
  const formRef = useRef(null);
  const scrollToForm = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const benefits = [
    { icon: Eye, title: "Drive Safer at Night", body: "Cloudy lenses cut your light output dramatically. Restoration brings back bright, clear visibility when it matters most." },
    { icon: Sparkles, title: "Like-New Clarity", body: "Multi-stage sanding and polishing removes years of yellowing and oxidation for a crisp, factory-fresh look." },
    { icon: BadgeDollarSign, title: "Save vs. Replacement", body: "New headlight assemblies can cost hundreds. Restoration gets the same clarity for $80–$180 per vehicle." },
    { icon: Truck, title: "We Come To You", body: "Mobile service at your home or work. No dropping the car off, no waiting room — most jobs done in about an hour." },
    { icon: ShieldCheck, title: "3-Year Warranty", body: "If your headlights fail within 3 years, we redo them free. We stand behind every job, no fine print." },
    { icon: Star, title: "Owner-Operated", body: "You work directly with Alex Koehn — local, dependable, and personally invested in doing it right." },
  ];

  const steps = [
    { icon: Camera, title: "Send a Photo", body: "Text or email a quick picture of your headlights with your vehicle info for a fast, free estimate." },
    { icon: MapPin, title: "We Come To You", body: "We schedule a time that works and arrive at your home or workplace — fully mobile." },
    { icon: Wrench, title: "Restore & Protect", body: "Professional sanding, polishing, and a protective coating for lasting clarity." },
    { icon: CheckCircle2, title: "Drive Clear", body: "Inspect the results together — backed by a 3-year warranty for total peace of mind." },
  ];

  const trust = [
    { icon: BadgeDollarSign, title: "$80–$180", sub: "Per vehicle" },
    { icon: Truck, title: "Mobile Service", sub: "We come to you" },
    { icon: ShieldCheck, title: "3-Year Warranty", sub: "Free redo if it fails" },
    { icon: MapPin, title: "100-Mile Radius", sub: "From Hiawatha, KS" },
  ];

  const faqs = [
    { q: "How long does the restoration last?", a: "Unlike cheap DIY kits that fade in a few weeks, our multi-step professional process removes the damaged plastic entirely and seals it with a high-grade UV block. Every restoration is backed by a 3-year warranty." },
    { q: "Do I need to bring my vehicle to a shop?", a: "Nope! We are 100% mobile. We bring all of our specialized equipment straight to your home or workplace and do the job right in the driveway while you go about your day." },
    { q: "How long does the service take?", a: "A standard full restoration takes about 45 to 60 minutes. Your car is ready to drive immediately afterward." },
  ];

  return (
    <div className="App bg-[#070B14] text-white min-h-screen overflow-x-hidden">
      <Toaster position="top-center" richColors />
      <Header phone={PHONE} onQuote={scrollToForm} />
      <StickyMobileCTA phone={PHONE} smsHref={SMS_HREF} />

      <span id="top" />

      {/* HERO */}
      <section className="relative noise pt-28 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(900px 420px at 18% 8%, rgba(59,155,232,0.20) 0%, rgba(59,155,232,0.06) 35%, rgba(7,11,20,0) 70%), radial-gradient(700px 380px at 88% 18%, rgba(255,255,255,0.05) 0%, rgba(7,11,20,0) 60%)",
          }}
          aria-hidden="true"
        />
        <Container className="relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#3B9BE8]/30 bg-[#3B9BE8]/10 px-3 py-1.5 text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase text-[#7Fc1ef]">
                  <Sun className="h-3.5 w-3.5" /> Mobile Headlight Restoration
                </div>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[0.01em] text-white">
                  Crystal-Clear Headlights —
                  <span className="text-[#3B9BE8]"> Without Leaving Home.</span>
                </h1>
                <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
                  Foggy, yellowed headlights restored to like-new clarity. Serving St. Joseph, MO, Hiawatha, KS and nearby towns within 100 miles.
                  <span className="text-white font-semibold"> $80–$180 per vehicle</span>, backed by a 3-year warranty.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a
                    data-testid={LANDING.heroGetQuote}
                    href={SMS_HREF}
                    className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[#3B9BE8] text-[#070B14] font-bold text-base hover:bg-[#2F8FE0] hover:-translate-y-0.5 transition-[transform,background-color] shadow-[0_18px_50px_rgba(59,155,232,0.18)]"
                  >
                    Get a Free Quote
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                  <a
                    data-testid={LANDING.heroCallNow}
                    href={`tel:${PHONE}`}
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-white/16 text-white font-semibold hover:border-[#3B9BE8]/50 hover:bg-[#3B9BE8]/10 transition-colors"
                  >
                    <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
                  </a>
                </div>
                <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/55">
                  <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#3B9BE8]" /> 3-Year Warranty</span>
                  <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-[#3B9BE8]" /> We Come To You</span>
                  <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#3B9BE8]" /> ~1 Hour Service</span>
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-6">
              <BeforeAfterShowcase />
            </div>
          </div>
        </Container>
      </section>

      {/* TRUST STRIP */}
      <section className="py-8 border-y border-white/8 bg-[#0B1224]/60">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trust.map((t, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.8, delay: i * 0.08, ease: "easeOut" }}
                className="flex items-center gap-3 rounded-2xl border border-white/8 bg-[#0B1224] p-4">
                <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3B9BE8]/12">
                  <t.icon className="h-5 w-5 text-[#3B9BE8]" />
                </div>
                <div>
                  <div data-testid={i === 0 ? LANDING.pricingValue : undefined} className="font-display text-xl text-white tracking-wide leading-none">{t.title}</div>
                  <div className="text-xs text-white/55 mt-1">{t.sub}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY US / BENEFITS */}
      <section id="why" className="py-16 sm:py-20">
        <Container>
          <SectionHeader
            eyebrow="Why Restore"
            title="Brighter, Safer, Better-Looking"
            subtitle="Restoring your headlights is one of the most affordable upgrades you can make — for safety and for looks."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: "easeOut" }}
                className="rounded-2xl border border-white/8 bg-[#0B1224] p-6 hover:border-[#3B9BE8]/30 hover:shadow-[0_18px_50px_rgba(59,155,232,0.10)] transition-[border-color,box-shadow]">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#3B9BE8]/12">
                  <b.icon className="h-5 w-5 text-[#3B9BE8]" />
                </div>
                <h3 className="mt-4 font-display text-xl tracking-wide text-white">{b.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-16 sm:py-20 bg-[#0B1224]/50 border-y border-white/8">
        <Container>
          <SectionHeader eyebrow="How It Works" title="Simple, Mobile, Done in About an Hour" />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.8, delay: i * 0.1, ease: "easeOut" }}
                className="relative rounded-2xl border border-white/8 bg-[#070B14] p-6">
                <div className="absolute top-5 right-5 font-display text-3xl text-white/10">0{i + 1}</div>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#3B9BE8]/12">
                  <s.icon className="h-5 w-5 text-[#3B9BE8]" />
                </div>
                <h3 className="mt-4 font-display text-xl tracking-wide text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.body}</p>
                {i === 0 && (
                  <a
                    data-testid={LANDING.stepOneTextLink}
                    href={SMS_HREF}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3B9BE8] hover:text-[#7Fc1ef] transition-colors"
                  >
                    Text a Photo Now <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* ABOUT */}
      <section className="py-16 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="text-[10px] sm:text-xs text-[#3B9BE8] font-semibold tracking-[0.22em] uppercase">Meet The Owner</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-[0.02em] leading-[1.05]">
                Alex Koehn — Owner & Technician
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                Koehn Headlight Restoration is a locally owned, owner-operated business. When you book, you work directly with Alex —
                no call centers, no middlemen. Every job is done by hand with professional-grade products and finished with a
                protective coating built to last.
              </p>
              <p className="mt-4 text-white/70 leading-relaxed">
                The goal is simple: honest work, fair pricing, and headlights that look and perform like new — all backed by a
                3-year warranty. If they ever fail within 3 years, Alex makes it right for free.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[#3B9BE8] text-[#070B14] font-bold hover:bg-[#2F8FE0] transition-colors">
                  <Phone className="h-4 w-4" /> Call Alex
                </a>
                <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-white/16 text-white hover:bg-white/5 transition-colors">
                  <Mail className="h-4 w-4" /> Email Alex
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <motion.div {...fadeUp} className="rounded-2xl border border-white/8 bg-[#0B1224] p-8 flex flex-col items-center text-center">
                <LogoLockup size="md" align="center" />
                <div className="mt-6 w-full border-t border-white/8 pt-5 space-y-3 text-sm">
                  <a href={`tel:${PHONE}`} className="flex items-center justify-center gap-2 text-white/80 hover:text-white">
                    <Phone className="h-4 w-4 text-[#3B9BE8]" /> {PHONE_DISPLAY}
                  </a>
                  <a href={`mailto:${EMAIL}`} className="flex items-center justify-center gap-2 text-white/80 hover:text-white break-all">
                    <Mail className="h-4 w-4 text-[#3B9BE8]" /> {EMAIL}
                  </a>
                  <div className="flex items-center justify-center gap-2 text-white/80">
                    <MapPin className="h-4 w-4 text-[#3B9BE8]" /> Mobile — 100 mi of Hiawatha, KS
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      {/* SERVICE AREA */}
      <section id="area" className="py-16 sm:py-20 bg-[#0B1224]/50 border-y border-white/8">
        <Container>
          <div data-testid={LANDING.serviceAreaBlock}>
            <SectionHeader
              eyebrow="Service Area"
              title="Serving St. Joseph, MO & NE Kansas"
              subtitle="Fully mobile — we come to you anywhere within a 100-mile radius of Hiawatha, KS, including St. Joseph, MO and the surrounding NW Missouri and Northeast Kansas communities. Not sure if you're covered? Just ask."
            />
            <div className="mt-10 flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
              {TOWNS.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#070B14] px-4 py-2 text-sm text-white/75">
                  <MapPin className="h-3.5 w-3.5 text-[#3B9BE8]" /> {t}
                </span>
              ))}
              <span className="inline-flex items-center rounded-full border border-[#3B9BE8]/30 bg-[#3B9BE8]/10 px-4 py-2 text-sm text-[#7Fc1ef] font-medium">
                + more within 100 miles
              </span>
            </div>
          </div>
        </Container>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 sm:py-20">
        <Container>
          <SectionHeader eyebrow="Pricing" title="Simple, Honest Pricing" subtitle="No surprises. One flat range that covers most vehicles, with mobile service included." />
          <div className="mt-12 max-w-lg mx-auto">
            <motion.div {...fadeUp} className="relative rounded-3xl border border-[#3B9BE8]/30 bg-[#0B1224] p-8 text-center shadow-[0_0_0_1px_rgba(59,155,232,0.18),0_24px_70px_rgba(0,0,0,0.45)]">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#3B9BE8]/12 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#7Fc1ef]">
                <Truck className="h-3.5 w-3.5" /> Mobile Service Included
              </div>
              <div className="mt-5 font-display text-6xl text-white tracking-wide">
                $80<span className="text-white/40 text-3xl align-top">–</span>$180
              </div>
              <div className="text-white/55 mt-1">per vehicle</div>
              <ul className="mt-7 space-y-3 text-left">
                {[
                  "Both headlights professionally restored",
                  "Multi-stage sanding & polishing",
                  "Protective UV coating applied",
                  "We come to your home or workplace",
                  "Backed by a 3-year warranty",
                ].map((li) => (
                  <li key={li} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 className="h-5 w-5 text-[#3B9BE8] shrink-0" /> {li}
                  </li>
                ))}
              </ul>
              <a href={SMS_HREF} className="mt-8 flex items-center justify-center w-full h-12 rounded-xl bg-[#3B9BE8] text-[#070B14] font-bold hover:bg-[#2F8FE0] transition-colors">
                Get My Free Quote
              </a>
              <p className="mt-3 text-xs text-white/45">Final price depends on headlight size & condition.</p>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* WARRANTY */}
      <section className="py-12">
        <Container>
          <motion.div data-testid={LANDING.warrantyCallout} {...fadeUp}
            className="relative overflow-hidden rounded-3xl border border-[#3B9BE8]/25 bg-gradient-to-br from-[#0B1224] to-[#0E1730] p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#3B9BE8]/15">
                <ShieldCheck className="h-8 w-8 text-[#3B9BE8]" />
              </div>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl text-white tracking-wide">Backed by a 3-Year Warranty</h3>
                <p className="mt-2 text-white/70 leading-relaxed max-w-2xl">
                  We stand behind every restoration. If your headlights cloud back up or the finish fails within three years, we'll
                  redo them <span className="text-white font-semibold">completely free</span>. No fine print, no hassle.
                </p>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* CONTACT / FORM */}
      <section id="contact" ref={formRef} className="py-16 sm:py-20 bg-[#0B1224]/50 border-y border-white/8">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="text-[10px] sm:text-xs text-[#3B9BE8] font-semibold tracking-[0.22em] uppercase">Get Started</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-[0.02em] leading-[1.05]">
                Request Your Free Quote
              </h2>
              <p className="mt-4 text-white/70 leading-relaxed">
                Fill out the quick form and Alex will get back to you — usually the same day. Prefer to talk? Call or text anytime.
              </p>
              <div className="mt-7 space-y-3">
                <a href={`tel:${PHONE}`} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-[#070B14] p-4 hover:border-[#3B9BE8]/30 transition-colors">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#3B9BE8]/12"><Phone className="h-5 w-5 text-[#3B9BE8]" /></div>
                  <div><div className="text-xs text-white/50">Call or Text</div><div className="text-white font-semibold">{PHONE_DISPLAY}</div></div>
                </a>
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-[#070B14] p-4 hover:border-[#3B9BE8]/30 transition-colors">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#3B9BE8]/12"><Mail className="h-5 w-5 text-[#3B9BE8]" /></div>
                  <div><div className="text-xs text-white/50">Email</div><div className="text-white font-semibold break-all">{EMAIL}</div></div>
                </a>
              </div>
            </div>
            <div className="lg:col-span-7">
              <QuoteForm />
            </div>
          </div>
        </Container>
      </section>

      {/* REAL RESULTS */}
      <section id="results" className="py-16 sm:py-20 bg-[#0B1224]/50 border-y border-white/8">
        <Container>
          <SectionHeader
            eyebrow="Real Results"
            title="See The Difference"
            subtitle="Real work, real clarity — a look at actual jobs. Swipe through to see more before-and-afters."
          />
          <motion.div {...fadeUp} className="mt-12">
            <Carousel opts={{ align: "start", loop: true }} className="max-w-4xl mx-auto px-1 sm:px-0">
              <CarouselContent className="-ml-3">
                {GALLERY.map((g, i) => (
                  <CarouselItem key={i} className="pl-3 basis-full sm:basis-4/5 lg:basis-3/4">
                    <div className="relative rounded-2xl overflow-hidden border border-[#3B9BE8]/30 bg-[#070B14] shadow-[0_0_0_1px_rgba(59,155,232,0.18),0_18px_50px_rgba(0,0,0,0.5)]">
                      <div className="h-[300px] sm:h-[440px] w-full flex items-center justify-center bg-[#070B14]">
                        <img
                          src={g.src}
                          alt={g.alt}
                          loading="lazy"
                          className="max-h-full max-w-full w-auto h-auto object-contain select-none"
                        />
                      </div>
                      {g.type === "ba" && (
                        <>
                          <span className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/90 border border-white/15">Before</span>
                          <span className="absolute top-3 right-3 rounded-full bg-[#3B9BE8] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#070B14]">After</span>
                        </>
                      )}
                      {g.type === "after" && (
                        <span className="absolute top-3 right-3 rounded-full bg-[#3B9BE8] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#070B14]">After</span>
                      )}
                      {g.type === "progress" && (
                        <span className="absolute top-3 right-3 rounded-full bg-[#F5A524] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#070B14]">In Progress</span>
                      )}
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10">
                        <p className="text-sm text-white/90">{g.caption}</p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex -left-4 lg:-left-12 bg-[#0B1224] border-white/15 text-white hover:bg-[#3B9BE8] hover:text-[#070B14]" />
              <CarouselNext className="hidden sm:flex -right-4 lg:-right-12 bg-[#0B1224] border-white/15 text-white hover:bg-[#3B9BE8] hover:text-[#070B14]" />
            </Carousel>
            <p className="mt-4 text-center text-xs text-white/45 sm:hidden">← Swipe to see more →</p>
            <div className="mt-7 text-center">
              <a
                href={SMS_HREF}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[#3B9BE8] text-[#070B14] font-bold hover:bg-[#2F8FE0] transition-colors"
              >
                Get Results Like This <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionHeader eyebrow="FAQ" title="Common Questions" />
          <div className="mt-10 max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-white/8 bg-[#0B1224] px-5">
                  <AccordionTrigger className="text-left text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/65 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="relative noise border-t border-white/8 bg-[#070B14] pt-14 pb-28 lg:pb-14">
        <Container className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <LogoLockup size="md" />
              <p className="mt-4 text-sm text-white/55 max-w-xs leading-relaxed">
                Mobile headlight restoration serving St. Joseph, MO, Hiawatha, KS and surrounding towns within 100 miles.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider">Contact</h4>
              <div className="mt-4 space-y-3 text-sm">
                <a data-testid={LANDING.footerCall} href={`tel:${PHONE}`} className="flex items-center gap-2 text-white/70 hover:text-white">
                  <Phone className="h-4 w-4 text-[#3B9BE8]" /> {PHONE_DISPLAY}
                </a>
                <a data-testid={LANDING.footerEmail} href={`mailto:${EMAIL}`} className="flex items-center gap-2 text-white/70 hover:text-white break-all">
                  <Mail className="h-4 w-4 text-[#3B9BE8]" /> {EMAIL}
                </a>
                <div className="flex items-center gap-2 text-white/70">
                  <MapPin className="h-4 w-4 text-[#3B9BE8]" /> Hiawatha, Kansas (mobile)
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider">Service</h4>
              <ul className="mt-4 space-y-2 text-sm text-white/60">
                <li>Headlight Restoration</li>
                <li>Mobile / Come-to-You</li>
                <li>$80–$180 per vehicle</li>
                <li>3-Year Warranty</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-white/8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/45">
            <span>© {new Date().getFullYear()} Koehn Headlight Restoration. All rights reserved.</span>
            <span>Owner & Technician: Alex Koehn</span>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default App;
